import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import { getClinicBrandCommsQuota } from 'src/clinic/services/commsQuotaService'

const CommsQuotaDialog = ({ open, onClose, clinicBrands = [] }) => {
  const [selectedBrandId, setSelectedBrandId] = useState('')
  const [quotaData, setQuotaData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchQuota = useCallback(async (brandId) => {
    if (!brandId) return
    
    setLoading(true)
    setError(null)
    try {
      const data = await getClinicBrandCommsQuota(brandId)
      setQuotaData(data)
    } catch (err) {
      setError(err.message || 'Failed to load communication quota')
      setQuotaData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Reset and set first brand as default when dialog opens
  useEffect(() => {
    if (open && clinicBrands.length > 0) {
      const firstBrandId = clinicBrands[0]?.id || ''
      setSelectedBrandId(firstBrandId)
      if (firstBrandId) {
        fetchQuota(firstBrandId)
      }
    } else if (!open) {
      // Reset state when dialog closes
      setSelectedBrandId('')
      setQuotaData(null)
      setError(null)
    }
  }, [open, clinicBrands, fetchQuota])

  // Fetch quota when brand is manually changed
  const handleBrandChange = (event) => {
    const newBrandId = event.target.value
    setSelectedBrandId(newBrandId)
    if (newBrandId) {
      fetchQuota(newBrandId)
    }
  }


  const handleRefresh = () => {
    if (selectedBrandId) {
      fetchQuota(selectedBrandId)
    }
  }

  const selectedBrand = clinicBrands.find((brand) => brand.id === selectedBrandId)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Communication Quota
          </Typography>
          {clinicBrands.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={selectedBrandId}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {clinicBrands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.title || brand.brandName || brand.clinicBrandName || brand.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : quotaData ? (
          <Box>
            {selectedBrand && (
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {selectedBrand.title || selectedBrand.brandName || selectedBrand.clinicBrandName}
              </Typography>
            )}

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(16, 185, 129, 0.04)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    WhatsApp Credits
                  </Typography>
                  <Typography variant="h4" fontWeight={600} color="primary">
                    {quotaData.waCredits ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    SMS Credits
                  </Typography>
                  <Typography variant="h4" fontWeight={600} color="info.main">
                    {quotaData.smsCredits ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(168, 85, 247, 0.04)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Email Credits
                  </Typography>
                  <Typography variant="h4" fontWeight={600} color="secondary">
                    {quotaData.emailCredits ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(245, 158, 11, 0.04)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Push Credits
                  </Typography>
                  <Typography variant="h4" fontWeight={600} sx={{ color: '#f59e0b' }}>
                    {quotaData.pushCredits ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(239, 68, 68, 0.04)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Outcall Credits
                  </Typography>
                  <Typography variant="h4" fontWeight={600} color="error">
                    {quotaData.outcallCredits ?? 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {quotaData.latestCreditPurchaseId && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Latest Purchase ID: {quotaData.latestCreditPurchaseId}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleRefresh} disabled={loading || !selectedBrandId}>
          Refresh
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CommsQuotaDialog

